import React from "react";
import { FiMapPin } from "react-icons/fi";
import Logo from "../styles/Black-SVG-Landscape.svg";
import ChrisPhoto from "../candidates/KIMBERLYP.jpg";



export default function HeadOfPartnershipsKimberlyPotter() {
 const candidateProfileCopy = {
  name: "Kim",
  location: "Pittsburgh, PA",
  compensation: {
    range: "$150K base, $180K+ OTE target",
  },
  availability: "Availability to be confirmed",

  working_style:
    "Kim is a systems thinker who treats every partnership as something you build once and run for years. She leads with listening and trust, building genuine relationships before she ever makes an ask, then puts in the structure to keep them healthy and growing: regular check ins, clear health metrics, and steady expansion over time. She has spent her career inside healthcare and telehealth, has a real feel for how prescribers and clinics work, and is fluent in the compliance side that comes with it.",

  loom_video_link: "https://fathom.video/share/ve9zdMqBaZzujirKYPzanA8Ft8aLy4rm",

  tools: [
    {
      category: "Partnership & Referral Development",
      items: [
        "Building referral pipelines that became top growth channels",
        "Winning provider trust with a patient, listening first approach",
        "Growing partnerships from first touch through long term renewal",
      ],
    },
    {
      category: "Growth & Revenue Systems",
      items: [
        "Turning adoption and retention into steady revenue growth",
        "Upsell, cross sell, and expansion programs",
        "Clear metrics for partner health and prioritization",
      ],
    },
    {
      category: "Team Leadership",
      items: [
        "Coaching reps and holding the team accountable",
        "Diagnosing why performance dips and resetting approach",
        "Building operations and processes from scratch",
      ],
    },
    {
      category: "Healthcare & Compliance",
      items: [
        "Telehealth and behavioral health experience",
        "Deep HIPAA, privacy, and anti kickback knowledge",
        "Grew up in pharmacy and worked as a licensed provider",
      ],
    },
  ],

  soft_skills: [
    "15 plus years building partnerships and growth in healthcare",
    "Lifted provider adoption from 36 to 91 percent across 4,500 prescribers",
    "Systems builder who turns relationships into lasting revenue",
    "Deep regulated healthcare and compliance knowledge",
  ],

  about_candidate:
    "Kim has spent more than 15 years building partnerships, referral channels, and growth systems inside healthcare and telehealth companies. At a multi state telehealth platform she lifted provider adoption from 36 to 91 percent across 4,500 prescribers and opened a $12M expansion pipeline, and at an AI healthcare platform she built a referral engine that became a top growth channel. She thinks in repeatable systems, builds deep trust with providers, and knows the regulated, compliance heavy side of healthcare from the inside, having grown up around pharmacy and worked as a licensed provider herself.",

  core_strengths: [
    {
      headline: "Builds referral channels that move revenue",
      description:
        "At an AI healthcare platform Kim built a referral engine that became one of its top growth channels, and earlier she built a referral pipeline with a hospital program that grew 70 percent in 14 months while cutting wait times 41 percent. Turning relationships into a steady stream of new business is what she does best.",
    },
    {
      headline: "Wins trust the patient way",
      description:
        "Her approach is listening first and building genuine rapport before any ask, then earning the partnership through real value. That fits a market like fertility and women's health, where providers move on trust rather than pressure.",
    },
    {
      headline: "Thinks in repeatable systems",
      description:
        "Kim builds the structure that keeps partnerships healthy: regular check ins, clear health metrics, and a clear way to prioritize where to spend time. She has built these operations from scratch more than once, so she is comfortable starting from a blank page.",
    },
    {
      headline: "Knows the regulated, pharmacy side cold",
      description:
        "She grew up around her family's pharmacies, worked as a licensed provider, and has years of hands on experience with HIPAA, privacy, and the specific rules around healthcare partnerships and kickbacks. She already understands the use case for fertility and women's health prescriptions.",
    },
  ],

  relevant_experience: [
    {
      headline: "Chief Operating Officer | Skytherapist (Telehealth) | 2022 to 2023",
      bullets: [
        "Lifted provider adoption from 36 to 91 percent across 4,500 prescribers in 11 months",
        "Opened a $12M expansion pipeline by moving from a single payer to multiple channels",
        "Built the health scoring, check ins, and engagement programs that drove the growth",
      ],
    },
    {
      headline: "Chief Operating Officer | Clinician Assist (AI Healthcare) | 2023 to Present",
      bullets: [
        "Built a referral engine that became one of the company's top growth channels",
        "Drove a 35 percent revenue increase through a customer success and onboarding rebuild",
        "Doubled new user acquisition in a single quarter",
      ],
    },
    {
      headline: "Chief Clinical Officer | COR Behavioral Group | 2015 to 2022",
      bullets: [
        "Helped grow a multi state behavioral health provider to $20M in annual revenue",
        "Increased client retention 66 percent through better onboarding and lifecycle programs",
        "Beat onboarding timeline benchmarks by 75 percent every quarter for seven years",
      ],
    },
  ],
};

const technicalAssessment = {
  meta: {
    role: "Head of Healthcare Partnerships",
    interviewDate: "June 4, 2026",
    analyst: "AI Architechs Senior Hiring Analysis",
  },

  snapshot:
    "Kim is a builder of referral channels and growth systems inside healthcare. She wins providers over by listening and earning trust first, then puts in the structure to keep partnerships healthy and growing over time, and she knows the regulated, pharmacy side of this world from the inside. Her background is in telehealth and behavioral health rather than fertility specifically, though she already speaks fluently about the fertility and women's health opportunity. The main thing to confirm is that she is as energized by going out and winning brand new partnerships as she is by building the systems around them. Her pay expectations sit comfortably inside the range.",

  scorecard: [
    {
      dimension: "Partnership and Referral Channel Development",
      score: 9,
      rationale:
        "Her strongest story is a referral pipeline she built with a hospital program that grew 70 percent in 14 months and cut wait times 41 percent, and at an AI healthcare platform she built a referral engine that became a top growth channel. Turning relationships into real, repeatable new business is clearly her core strength.",
    },
    {
      dimension: "Building From Zero and Pipeline Construction",
      score: 8,
      rationale:
        "She gave a clear, repeatable approach to entering a new vertical, leading with trust and listening, then layering in check ins, health metrics, and a way to prioritize. She has built operations from scratch more than once, so a blank page does not faze her, with the hard outreach mechanics a touch less detailed than the relationship system.",
    },
    {
      dimension: "Revenue and Commercial Thinking",
      score: 8,
      rationale:
        "She thinks in clear frameworks, weighing money, referrals, and which partners open doors to bigger networks, and prioritizes by size, renewal likelihood, and effort required. Her track record is full of hard numbers, from upsell lifts to a $12M expansion pipeline, so the commercial instinct is well proven.",
    },
    {
      dimension: "Partner Conversion and Lifecycle Management",
      score: 9,
      rationale:
        "This is her wheelhouse. She walked through the full path from vetting and onboarding to a first successful cycle, then quarterly reviews, churn reduction, and steady expansion, and she is careful never to push so hard that she damages the relationship.",
    },
    {
      dimension: "SDR and Sales Team Leadership",
      score: 7,
      rationale:
        "She manages reps well, focusing on CRM discipline, coaching the reasons behind a slump, and measurable growth for each person. Her leadership has leaned toward customer success and operations more than running a dedicated outbound team, so there is some ramp on the pure SDR side.",
    },
    {
      dimension: "Regulated Healthcare Experience and Compliance",
      score: 9,
      rationale:
        "She grew up around her family's pharmacies, worked as a licensed provider, and knows HIPAA, privacy, and the specific rules around healthcare partnerships and kickbacks in detail. Compliance is second nature.",
    },
    {
      dimension: "Fertility and Women's Health Vertical Exposure",
      score: 7,
      rationale:
        "No direct fertility or women's health role, but she spoke more fluently than most about the actual use case, including the stigma around filling these prescriptions and how a pharmacy can solve it. Strong related understanding with a short ramp on the specific providers.",
    },
    {
      dimension: "Ambition and Drive",
      score: 8,
      rationale:
        "Clear builder energy. She has stood up operations and referral engines from scratch, owns outcomes rather than supporting them, and brings real initiative and ideas. Her pay expectations are reasonable and in range, with no sign of coasting.",
    },
  ],

  strengths: [
    {
      headline: "Builds referral channels that drive growth",
      description:
        "Kim has built referral engines and pipelines that became top growth channels, with concrete numbers behind them, which is the core of what this role is meant to do.",
    },
    {
      headline: "Earns trust the way this market rewards",
      description:
        "Her listen first, never push approach is well suited to fertility and women's health providers, who move on trust rather than pressure.",
    },
    {
      headline: "Comfortable starting from a blank page",
      description:
        "She has built customer success and growth operations from scratch more than once, so a brand new partnership channel is familiar territory.",
    },
    {
      headline: "Knows the regulated, pharmacy world",
      description:
        "Between a pharmacy upbringing, a license of her own, and years of hands on compliance work, she already understands the rules and the prescriber side.",
    },
  ],

  flags: [
    {
      headline: "More builder than hunter, worth confirming",
      description:
        "Kim's strength is building referral systems and deepening partnerships. It is worth confirming she is just as energized by going out and closing brand new partners from a cold start, not only nurturing and systematizing.",
    },
    {
      headline: "Fertility is adjacent, not direct",
      description:
        "Her healthcare experience is in telehealth and behavioral health rather than fertility, though she already speaks clearly about the fertility and women's health use case. A short ramp on the specific providers.",
    },
    {
      headline: "Thinks at a high, systems level",
      description:
        "She is a big picture, systems first thinker, which is a real strength, but worth confirming she will also own the day to day outreach work, not just design the system around it.",
    },
  ],

  when_to_hire: [
    "You want someone who has actually built referral channels that turned into real growth, and you value proven systems.",
    "You want a partner builder who wins on trust and listening, which fits how fertility and women's health providers tend to buy.",
    "You like that she can start from a blank page and build the repeatable system, since she has done it from scratch before.",
    "You want deep comfort with the regulated, pharmacy side of healthcare, including the compliance rules around partnerships.",
  ],

  when_to_be_cautious: [
    "You want a pure hunter who lives for cold outreach and closing brand new logos, since her strength leans toward building and deepening relationships and systems.",
    "You need someone who already knows fertility and women's health providers directly and can start those relationships immediately.",
    "You want someone who stays close to the hands on, daily outreach work rather than operating mostly at the strategy and systems level.",
  ],

  bottom_line:
    "Kim brings a strong record of building referral channels and growth systems inside healthcare, a trust first style that fits this market, and deep knowledge of the regulated, pharmacy side. The things to confirm are her appetite for winning brand new partners from a cold start and her ramp into fertility specifically, since her background is adjacent. On building the repeatable partnership engine this role needs, she is well equipped.",
};

  const ScorePill = ({ score }) => {
    let bg = "#f97316";
    if (score >= 8) bg = "#22c55e";
    else if (score >= 6) bg = "#36D1FF";
    return (
      <span
        style={{ backgroundColor: bg }}
        className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full"
      >
        {score} / 10
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#01014C] text-white flex flex-col items-center py-10 px-4">

      {/* ===================== PAGE 1 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative">

        {/* LEFT COLUMN */}
        <aside className="relative md:w-1/3 bg-gradient-to-b from-[#01014C] via-[#0A0A80] to-[#36D1FF] p-8 flex flex-col items-center text-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#36D1FF]/30 to-transparent"></div>

          {/* Avatar placeholder */}
          <div className="relative mb-4">
            <div className="absolute inset-0 rounded-full bg-[#36D1FF]/20 blur-2xl"></div>
            <div className="relative w-36 h-36 rounded-full border-4 border-[#36D1FF] shadow-lg overflow-hidden">
              <img src={ChrisPhoto} alt="Chris Weston" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Name */}
          <h1 className="text-2xl font-semibold mb-1 text-[#FFFDFB]">
            {candidateProfileCopy.name}
          </h1>

          {/* Location */}
          <div className="flex items-center gap-2 text-[#E0F7FF] text-sm mb-6 opacity-90">
            <FiMapPin className="text-[#36D1FF]" />
            <span>{candidateProfileCopy.location}</span>
          </div>

          {/* Compensation */}
          <div className="bg-[#01014C] border border-[#36D1FF]/50 rounded-xl p-4 w-full text-left mb-4">
            <p className="text-[#B3E9FF] text-sm font-semibold uppercase mb-1">
              Compensation
            </p>
            <p className="text-[#36D1FF] text-lg font-bold">
              {candidateProfileCopy.compensation.range}
            </p>
          </div>

          {/* Availability */}
          <div className="bg-[#01014C] border border-[#36D1FF]/30 rounded-xl p-4 w-full text-left mb-6">
            <p className="text-[#E0F7FF] text-sm">
              {candidateProfileCopy.availability}
            </p>
          </div>

          {/* Watch Interview Button */}
          <a
            href={candidateProfileCopy.loom_video_link}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full mb-6 flex items-center justify-center gap-2 bg-gradient-to-r from-[#36D1FF] to-[#0099CC] hover:from-[#5DDDFF] hover:to-[#00AADD] text-[#01014C] font-bold text-sm py-3 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-[#36D1FF]/20 hover:shadow-[#36D1FF]/40"
          >
            ▶ Watch Interview
          </a>

          {/* Tools */}
          <div className="w-full mt-4 mb-6 text-left">
            <h3 className="text-sm font-semibold mb-3 text-[#B3E9FF] uppercase">
              Tools & Capabilities
            </h3>
            {candidateProfileCopy.tools.map((group, i) => (
              <div key={i} className="mb-4">
                <p className="text-xs font-semibold text-[#36D1FF] mb-1 uppercase tracking-wide">
                  {group.category}
                </p>
                <ul className="space-y-1">
                  {group.items.map((tool, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-2 text-xs text-[#E0F7FF]/90 leading-snug"
                    >
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#36D1FF] shrink-0" />
                      {tool}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Soft Skills */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Key Profile Highlights
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-[#E0F7FF] text-sm">
              {candidateProfileCopy.soft_skills.map((skill, i) => (
                <li key={i}>{skill}</li>
              ))}
            </ul>
          </div>

          {/* Logo placeholder */}
          <div className="w-full mt-8 flex justify-center">
            <img src={Logo} alt="AI Architechs" className="h-16 opacity-80" />
          </div>
        </aside>

        {/* RIGHT COLUMN */}
        <main className="flex-1 p-10 text-[#01014C] space-y-10">
          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              About this Candidate
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {candidateProfileCopy.about_candidate}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Core Strengths
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {candidateProfileCopy.core_strengths.map((item, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold mb-1">{item.headline}</p>
                  <p className="text-sm text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Relevant Experience
            </h2>
            <div className="space-y-6">
              {candidateProfileCopy.relevant_experience.map((exp, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold text-[#EB5D2A] mb-2">{exp.headline}</p>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
                    {exp.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* ===================== PAGE BREAK BAND ===================== */}
      <div className="w-full py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-10 rounded-2xl bg-gradient-to-r from-[#01014C] via-[#0A0A80] to-[#36D1FF] opacity-70 shadow-[0_0_40px_#36D1FF20]" />
        </div>
      </div>

      {/* ===================== PAGE 2 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl pb-2">
        <main className="p-10 text-[#01014C]">
          <div className="border border-[#36D1FF]/25 rounded-2xl p-6 bg-white shadow-[0_0_12px_#36D1FF15]">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6">
              <h2 className="text-2xl font-semibold text-[#01014C]">
                Technical Analysis
              </h2>
              <div className="flex flex-col items-start md:items-end gap-1 text-xs text-gray-400">
                <span>{technicalAssessment.meta.role}</span>
                <span>Interview: {technicalAssessment.meta.interviewDate}</span>
                <span>{technicalAssessment.meta.analyst}</span>
              </div>
            </div>

            {/* Snapshot */}
            <div className="border border-[#36D1FF]/20 rounded-xl p-5 bg-[#36D1FF]/5 mb-6">
              <p className="text-base font-bold text-[#01014C] mb-2">Executive Fit Snapshot</p>
              <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
                {technicalAssessment.snapshot}
              </p>
            </div>

            {/* Strengths & Flags */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Strengths
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.strengths.map((item, i) => (
                    <div key={i} className="border border-[#22c55e]/30 rounded-xl p-4 bg-[#22c55e]/5">
                      <p className="font-semibold text-sm text-[#01014C] mb-1">{item.headline}</p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Flags / Watch-outs
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.flags.map((item, i) => (
                    <div key={i} className="border border-[#f97316]/30 rounded-xl p-4 bg-[#f97316]/5">
                      <p className="font-semibold text-sm text-[#01014C] mb-1">{item.headline}</p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Scorecard */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                Quantitative Scorecard
              </h3>
              <div className="overflow-x-auto rounded-xl border border-[#36D1FF]/25">
                <table className="w-full text-sm">
                  <thead className="bg-[#36D1FF]/10 text-[#01014C]">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Dimension</th>
                      <th className="text-left font-semibold px-4 py-3 w-[120px]">Score</th>
                      <th className="text-left font-semibold px-4 py-3">Rationale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#36D1FF]/15">
                    {technicalAssessment.scorecard.map((row, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="px-4 py-3 font-medium text-[#01014C] align-top">{row.dimension}</td>
                        <td className="px-4 py-3 align-top"><ScorePill score={row.score} /></td>
                        <td className="px-4 py-3 text-gray-700 whitespace-pre-line align-top">{row.rationale}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* When to Hire / Cautions */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Hire if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_hire.map((item, i) => (
                    <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-4 bg-[#36D1FF]/5 text-sm text-gray-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Be cautious or pass if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_be_cautious.map((item, i) => (
                    <div key={i} className="border border-[#f59e0b]/30 rounded-xl p-4 bg-[#f59e0b]/5 text-sm text-gray-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Line */}
            <div className="border border-[#01014C]/20 rounded-xl p-5 bg-[#01014C]/5">
              <p className="text-sm font-bold text-[#01014C] mb-2 uppercase tracking-wide">
                🏁 Bottom Line
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {technicalAssessment.bottom_line}
              </p>
            </div>

          </div>
        </main>
      </div>

    </div>
  );
}